import type { GuardMode, GuardProbeProfile, GuardSignalKind } from '@nadoutong/contracts';
import { useT } from '../../../i18n/index.ts';
import { cx } from '../../../ui/index.ts';
import { ModeSegment, NumberField, Segment } from './GuardControls.tsx';
import { GuardTripFields } from './GuardTripFields.tsx';
import type { GuardIssue, OrgDraft, OrgSignalDraft, ProfileDraft, SkuDraft, SkuSignalDraft } from './guardModel.ts';
import { GUARD_SIGNAL_VIEWS, type GuardSignalView } from './guardSignals.ts';

const PREFIX = 'guaranteed-guard-org';

function modeKey(mode: GuardMode): 'guaranteed.guard.mode.off' | 'guaranteed.guard.mode.shadow' | 'guaranteed.guard.mode.act' {
  return mode === 'act' ? 'guaranteed.guard.mode.act' : mode === 'shadow' ? 'guaranteed.guard.mode.shadow' : 'guaranteed.guard.mode.off';
}

function skuDisposalText(view: GuardSignalView, sku: SkuSignalDraft, t: ReturnType<typeof useT>): string {
  const mode = t(modeKey(sku.walk));
  if (view.spec.threshold === null || sku.walk === 'off') return mode;
  return `${sku.threshold} ${t('guaranteed.guard.unit.seconds')} · ${mode}`;
}

function DisposalCell({
  view,
  draft,
  sku,
  issues,
  disabled,
  onChange,
  onEnter,
}: {
  readonly view: GuardSignalView;
  readonly draft: OrgSignalDraft;
  readonly sku: SkuSignalDraft;
  readonly issues: ReadonlyMap<string, GuardIssue>;
  readonly disabled: boolean;
  readonly onChange: (next: OrgSignalDraft) => void;
  readonly onEnter?: () => void;
}): JSX.Element {
  const t = useT();
  const kind = view.spec.kind;
  if (view.spec.walk === 'none') return <span className="text-dense text-paper-low">—</span>;
  return (
    <div className="flex flex-col gap-1">
      <Segment
        value={draft.disposal}
        disabled={disabled}
        ariaLabel={t('guaranteed.guard.org.col.disposal')}
        testId={`${PREFIX}-${kind}-disposal`}
        onChange={(disposal) => onChange({ ...draft, disposal })}
        options={[
          { value: 'inherit', label: t('guaranteed.guard.org.disposal.inherit') },
          { value: 'custom', label: t('guaranteed.guard.org.disposal.custom'), tone: 'bg-signal-dim text-signal' },
        ]}
      />
      {draft.disposal === 'inherit' ? (
        <span className="text-micro text-paper-low" data-testid={`${PREFIX}-${kind}-sku-value`}>
          {t('guaranteed.guard.org.sku_value', { value: skuDisposalText(view, sku, t) })}
        </span>
      ) : (
        <>
          <ModeSegment
            value={draft.walk}
            disabled={disabled}
            ariaLabel={view.walkTitle === null ? '' : t(view.walkTitle)}
            testId={`${PREFIX}-${kind}-walk`}
            onChange={(walk) => onChange({ ...draft, walk })}
          />
          {view.thresholdLabel === null ? null : (
            <NumberField
              label={t(view.thresholdLabel)}
              unit="guaranteed.guard.unit.seconds"
              value={draft.threshold}
              issue={issues.get(`${kind}.threshold`)}
              disabled={disabled}
              testId={`${PREFIX}-${kind}-threshold`}
              onChange={(text) => onChange({ ...draft, threshold: text })}
              onEnter={onEnter}
            />
          )}
          {view.spec.retryAfterReasoning ? (
            <Segment
              value={draft.retryAfterReasoning ? 'yes' : 'no'}
              disabled={disabled}
              ariaLabel={t('guaranteed.guard.field.retry_after_reasoning')}
              testId={`${PREFIX}-${kind}-retry`}
              onChange={(value) => onChange({ ...draft, retryAfterReasoning: value === 'yes' })}
              options={[
                { value: 'no', label: t('guaranteed.guard.no') },
                { value: 'yes', label: t('guaranteed.guard.yes'), tone: 'bg-ok-dim text-ok' },
              ]}
            />
          ) : null}
          {view.spec.chatLane ? (
            <Segment
              value={draft.chatLane ? 'yes' : 'no'}
              disabled={disabled}
              ariaLabel={t('guaranteed.guard.field.chat_lane')}
              testId={`${PREFIX}-${kind}-chat-lane`}
              onChange={(value) => onChange({ ...draft, chatLane: value === 'yes' })}
              options={[
                { value: 'no', label: t('guaranteed.guard.no') },
                { value: 'yes', label: t('guaranteed.guard.yes'), tone: 'bg-ok-dim text-ok' },
              ]}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

function OrgRow({
  view,
  draft,
  sku,
  skuProfiles,
  issues,
  disabled,
  onChange,
  onEnter,
}: {
  readonly view: GuardSignalView;
  readonly draft: OrgSignalDraft;
  readonly sku: SkuSignalDraft;
  readonly skuProfiles: Readonly<Record<GuardProbeProfile, ProfileDraft>>;
  readonly issues: ReadonlyMap<string, GuardIssue>;
  readonly disabled: boolean;
  readonly onChange: (next: OrgSignalDraft) => void;
  readonly onEnter?: () => void;
}): JSX.Element {
  const t = useT();
  const kind: GuardSignalKind = view.spec.kind;
  const ownWithoutTrip = draft.globalTrip === 'own' && draft.trip.mode === 'off';
  // An org trip is released by the SKU's recover profile for this signal; the
  // write face does not cross-check the SKU row (final review, Opus P2-3), so say it here.
  const profileMissing = draft.trip.mode !== 'off' && !skuProfiles[sku.recoverProfile].enabled;
  return (
    <tr className="border-b border-line-soft align-top" data-testid={`${PREFIX}-row-${kind}`}>
      <td className="px-2.5 py-2 text-dense text-paper">{t(view.title)}</td>
      <td className="px-2.5 py-2">
        <DisposalCell view={view} draft={draft} sku={sku} issues={issues} disabled={disabled} onChange={onChange} onEnter={onEnter} />
      </td>
      <td className="px-2.5 py-2">
        <Segment
          value={draft.globalTrip}
          disabled={disabled}
          ariaLabel={t('guaranteed.guard.org.col.global')}
          testId={`${PREFIX}-${kind}-global`}
          onChange={(globalTrip) => onChange({ ...draft, globalTrip })}
          options={[
            { value: 'inherit', label: t('guaranteed.guard.org.global.inherit'), tone: 'bg-ok-dim text-ok' },
            { value: 'own', label: t('guaranteed.guard.org.global.own'), tone: 'bg-warn-dim text-warn' },
          ]}
        />
        {ownWithoutTrip ? (
          <p role="alert" className="m-0 mt-1 text-micro text-bad" data-testid={`${PREFIX}-${kind}-own-off-warn`}>
            {t('guaranteed.guard.org.own_off_warn')}
          </p>
        ) : null}
      </td>
      <td className="px-2.5 py-2">
        <div className="flex flex-col gap-1">
          <ModeSegment
            value={draft.trip.mode}
            disabled={disabled}
            ariaLabel={t('guaranteed.guard.org.col.own')}
            testId={`${PREFIX}-${kind}-trip`}
            onChange={(mode) => onChange({ ...draft, trip: { ...draft.trip, mode } })}
          />
          <div className={cx(draft.trip.mode === 'off' && 'hidden')}>
            <GuardTripFields
              kind={kind}
              prefix={PREFIX}
              trip={draft.trip}
              issues={issues}
              disabled={disabled}
              onChange={(trip) => onChange({ ...draft, trip })}
              onEnter={onEnter}
            />
            {profileMissing ? (
              <p role="alert" className="m-0 mt-1 text-micro text-bad" data-testid={`${PREFIX}-${kind}-profile-missing`}>
                {t('guaranteed.guard.org.profile_missing')}
              </p>
            ) : null}
            <Segment
              value={draft.borrow ? 'yes' : 'no'}
              disabled={disabled}
              ariaLabel={t('guaranteed.guard.org.borrow')}
              testId={`${PREFIX}-${kind}-borrow`}
              onChange={(value) => onChange({ ...draft, borrow: value === 'yes' })}
              options={[
                { value: 'yes', label: t('guaranteed.guard.org.borrow'), tone: 'bg-ok-dim text-ok' },
                { value: 'no', label: t('guaranteed.guard.no') },
              ]}
            />
          </div>
        </div>
      </td>
    </tr>
  );
}

export function GuardOrgTable({
  orgLabel,
  draft,
  sku,
  issues,
  disabled,
  onChange,
  onEnter,
}: {
  readonly orgLabel: string;
  readonly draft: OrgDraft;
  readonly sku: SkuDraft;
  readonly issues: ReadonlyMap<string, GuardIssue>;
  readonly disabled: boolean;
  readonly onChange: (next: OrgDraft) => void;
  readonly onEnter?: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-line bg-card p-4" data-testid="guaranteed-guard-org-table">
      <div>
        <h3 className="m-0 text-section text-paper">{t('guaranteed.guard.org.title', { org: orgLabel })}</h3>
        <p className="m-0 mt-1 text-label text-paper-mid">{t('guaranteed.guard.org.desc')}</p>
      </div>
      <table className="w-full border-collapse text-dense">
        <thead>
          <tr className="border-b border-line text-left text-label text-paper-low">
            <th className="px-2.5 py-1.5 font-normal">{t('guaranteed.guard.col.signal')}</th>
            <th className="px-2.5 py-1.5 font-normal">{t('guaranteed.guard.org.col.disposal')}</th>
            <th className="px-2.5 py-1.5 font-normal">{t('guaranteed.guard.org.col.global')}</th>
            <th className="px-2.5 py-1.5 font-normal">{t('guaranteed.guard.org.col.own')}</th>
          </tr>
        </thead>
        <tbody>
          {GUARD_SIGNAL_VIEWS.map((view) => (
            <OrgRow
              key={view.spec.kind}
              view={view}
              draft={draft.signals[view.spec.kind]}
              sku={sku.signals[view.spec.kind]}
              skuProfiles={sku.profiles}
              issues={issues}
              disabled={disabled}
              onChange={(next) => onChange({ ...draft, signals: { ...draft.signals, [view.spec.kind]: next } })}
              onEnter={onEnter}
            />
          ))}
        </tbody>
      </table>
      <p className="m-0 text-label text-paper-low">{t('guaranteed.guard.org.censor_note')}</p>
    </section>
  );
}
